<template>
	<section class="article-stream" aria-label="Volgende artikelen">
		<div
			v-for="item in items"
			:key="item.id"
			:ref="(element) => setItemElement(element, item.id)"
			class="article-stream__item"
			:data-article-stream-id="item.id"
			:data-article-stream-url="item.url"
		>
			<AsyncComponent :content="item.html" :device-context="deviceContext" />
		</div>

		<div v-if="loading && nearEnd" class="article-stream__status" role="status">
			<LoadingIndicator />
		</div>

		<div v-if="error" class="article-stream__fallback">
			<button type="button" class="uk-button uk-button-default" @click="retry">
				Opnieuw proberen
			</button>
		</div>

		<div ref="sentinel" class="article-stream__sentinel" aria-hidden="true"></div>
	</section>
</template>

<script>
import axios from 'axios';
import LazyLoad from 'vanilla-lazyload';
import AsyncComponent from './AsyncComponent.vue';
import LoadingIndicator from './LoadingIndicator.vue';

export default {
	name: 'ArticleStream',
	components: {
		AsyncComponent,
		LoadingIndicator,
	},
	props: {
		currentEntryId: {
			type: [Number, String],
			required: true,
		},
		currentUrl: {
			type: String,
			required: true,
		},
		currentTitle: {
			type: String,
			required: true,
		},
		currentDescription: {
			type: String,
			default: '',
		},
		endpoint: {
			type: String,
			default: '/async/article_next',
		},
		maxArticles: {
			type: Number,
			default: 5,
		},
	},
	data() {
		return {
			items: [],
			itemElements: new Map(),
			metadata: new Map(),
			prefetchedItem: null,
			cursorEntryId: Number(this.currentEntryId),
			loading: false,
			nearEnd: false,
			exhausted: false,
			error: false,
			activeUrl: this.currentUrl,
			loadObserver: null,
			scrollFrame: null,
			activationTimer: null,
			trackedEntryIds: new Set([Number(this.currentEntryId)]),
			trackingPixels: [],
		};
	},
	computed: {
		deviceContext() {
			return this.$root.device || {
				app: false,
				mobile: false,
				desktop: true,
			};
		},
	},
	mounted() {
		const initialArticle = document.querySelector('article.news-article:not(.article-stream__article)');

		if (initialArticle) {
			initialArticle.dataset.articleStreamId = String(this.currentEntryId);
			initialArticle.dataset.articleStreamUrl = this.currentUrl;
			this.metadata.set(String(this.currentEntryId), {
				id: Number(this.currentEntryId),
				url: this.currentUrl,
				title: this.currentTitle,
				description: this.currentDescription,
				trackingHtml: '',
			});
		}

		this.loadObserver = new IntersectionObserver((entries) => {
			this.nearEnd = entries.some((entry) => entry.isIntersecting);

			if (this.nearEnd) {
				this.appendPrefetchedItem();
			}
		}, {
			root: null,
			rootMargin: '0px 0px 1200px 0px',
			threshold: 0,
		});

		this.loadObserver.observe(this.$refs.sentinel);
		window.addEventListener('scroll', this.queueActiveArticleUpdate, { passive: true });
		window.addEventListener('resize', this.queueActiveArticleUpdate, { passive: true });

		this.prefetchNextArticle();
		this.queueActiveArticleUpdate();
	},
	beforeUnmount() {
		if (this.loadObserver) {
			this.loadObserver.disconnect();
		}

		window.removeEventListener('scroll', this.queueActiveArticleUpdate);
		window.removeEventListener('resize', this.queueActiveArticleUpdate);

		if (this.scrollFrame) {
			window.cancelAnimationFrame(this.scrollFrame);
		}

		if (this.activationTimer) {
			window.clearTimeout(this.activationTimer);
		}
	},
	methods: {
		setItemElement(element, itemId) {
			if (element) {
				this.itemElements.set(Number(itemId), element);
			} else {
				this.itemElements.delete(Number(itemId));
			}
		},
		async prefetchNextArticle() {
			if (this.loading || this.exhausted || this.prefetchedItem || this.items.length >= this.maxArticles) {
				return;
			}

			this.loading = true;
			this.error = false;

			try {
				const response = await axios.get(this.endpoint, {
					params: {
						entryId: this.cursorEntryId,
					},
				});

				if (!response.data || !response.data.found || !response.data.item) {
					this.exhausted = true;
					return;
				}

				this.prefetchedItem = response.data.item;
			} catch (error) {
				this.error = true;
				this.$logger(`app/component/article-stream/load-error:${error.message}`);
			} finally {
				this.loading = false;

				if (this.nearEnd && this.prefetchedItem) {
					this.appendPrefetchedItem();
				}
			}
		},
		appendPrefetchedItem() {
			if (!this.prefetchedItem || this.items.length >= this.maxArticles) {
				return;
			}

			const item = this.prefetchedItem;
			this.prefetchedItem = null;
			this.nearEnd = false;
			this.items.push(item);
			this.metadata.set(String(item.id), item);
			this.cursorEntryId = Number(item.id);

			this.$nextTick(() => {
				window.setTimeout(() => {
					this.hydrateItem(item.id);
					this.queueActiveArticleUpdate();
				}, 0);
			});

			this.prefetchNextArticle();
		},
		hydrateItem(itemId) {
			const element = this.itemElements.get(Number(itemId));

			if (!element) {
				return;
			}

			if (window.UIkit && typeof window.UIkit.update === 'function') {
				window.UIkit.update(element);
			}

			element.querySelectorAll('.lazy').forEach((lazyElement) => {
				LazyLoad.load(lazyElement);
			});

			if (window.instgrm && window.instgrm.Embeds) {
				window.instgrm.Embeds.process();
			}

			if (window.twttr && window.twttr.widgets) {
				window.twttr.widgets.load(element);
			}
		},
		queueActiveArticleUpdate() {
			if (this.scrollFrame) {
				return;
			}

			this.scrollFrame = window.requestAnimationFrame(() => {
				this.scrollFrame = null;
				this.checkLoadThreshold();
				this.updateActiveArticle();
			});
		},
		checkLoadThreshold() {
			if (!this.$refs.sentinel || this.exhausted || this.items.length >= this.maxArticles) {
				return;
			}

			if (this.$refs.sentinel.getBoundingClientRect().top <= window.innerHeight + 1200) {
				this.nearEnd = true;

				if (this.prefetchedItem) {
					this.appendPrefetchedItem();
				}
			}
		},
		updateActiveArticle() {
			const articleElements = Array.from(document.querySelectorAll('[data-article-stream-id]'));

			if (!articleElements.length) {
				return;
			}

			const activationLine = window.innerHeight * 0.28;
			let activeElement = articleElements[0];

			articleElements.forEach((element) => {
				if (element.getBoundingClientRect().top <= activationLine) {
					activeElement = element;
				}
			});

			const item = this.metadata.get(activeElement.dataset.articleStreamId);

			if (!item || item.url === this.activeUrl) {
				return;
			}

			this.activateArticle(item);
		},
		activateArticle(item) {
			this.activeUrl = item.url;

			const targetUrl = new URL(item.url, window.location.href);
			const historyUrl = targetUrl.origin === window.location.origin
				? `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
				: item.url;

			window.history.replaceState({
				...(window.history.state || {}),
				articleStream: true,
				entryId: item.id,
			}, '', historyUrl);

			document.title = item.title;
			this.updateMetaElement('link[rel="canonical"]', 'href', item.url);
			this.updateMetaElement('meta[name="description"]', 'content', item.description || '');

			window.dispatchEvent(new CustomEvent('article-stream:activate', {
				detail: {
					id: item.id,
					url: item.url,
					title: item.title,
				},
			}));

			if (this.activationTimer) {
				window.clearTimeout(this.activationTimer);
			}

			this.activationTimer = window.setTimeout(() => {
				if (this.activeUrl === item.url) {
					this.trackArticleView(item);
				}
			}, 800);
		},
		updateMetaElement(selector, attribute, value) {
			const element = document.querySelector(selector);

			if (element) {
				element.setAttribute(attribute, value);
			}
		},
		trackArticleView(item) {
			const entryId = Number(item.id);

			if (this.trackedEntryIds.has(entryId) || !item.trackingHtml) {
				return;
			}

			const parser = document.createElement('div');
			parser.innerHTML = item.trackingHtml;
			const source = parser.querySelector('img')?.getAttribute('src');

			if (!source) {
				return;
			}

			const pixel = new Image(1, 1);
			pixel.src = source;
			this.trackingPixels.push(pixel);
			this.trackedEntryIds.add(entryId);
		},
		retry() {
			this.error = false;
			this.prefetchNextArticle();
		},
	},
};
</script>
